import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Edit, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConversationStageDialog } from "./ConversationStageDialog";

interface ConversationStage {
  id: string;
  name: string;
  description: string;
  stage_type: "review" | "checkin" | "objective_setting" | "skills_assessment";
  order_index: number;
  timing_weeks: number;
  is_optional: boolean;
  template_id: string;
  conversation_templates: {
    name: string;
  };
}

export const ConversationStagesManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<ConversationStage | null>(null);

  const { data: stages, isLoading, refetch } = useQuery({
    queryKey: ['admin-conversation-stages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conversation_stages')
        .select(`
          id,
          name,
          description,
          stage_type,
          order_index,
          timing_weeks,
          is_optional,
          template_id,
          conversation_templates(name)
        `)
        .order('order_index');

      if (error) throw error;
      return data as ConversationStage[];
    },
  });

  const filteredStages = stages?.filter(stage =>
    stage.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stage.stage_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stage.conversation_templates.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (stage: ConversationStage) => {
    setSelectedStage(stage);
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedStage(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (stageId: string) => {
    if (!confirm("Are you sure you want to delete this stage?")) return;

    try {
      const { error } = await supabase
        .from('conversation_stages')
        .delete()
        .eq('id', stageId);

      if (error) throw error;
      refetch();
    } catch (error) {
      console.error('Error deleting stage:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search stages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-2" />
          Add Stage
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Stages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stages?.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Optional Stages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stages?.filter(s => s.is_optional).length || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Stage Types</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(stages?.map(s => s.stage_type)).size || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Stages ({filteredStages?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Template</TableHead>
                <TableHead>Timing</TableHead>
                <TableHead>Optional</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStages?.map((stage) => (
                <TableRow key={stage.id}>
                  <TableCell className="font-medium">{stage.order_index}</TableCell>
                  <TableCell>{stage.name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{stage.stage_type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{stage.conversation_templates.name}</Badge>
                  </TableCell>
                  <TableCell>{stage.timing_weeks} weeks</TableCell>
                  <TableCell>
                    <Badge variant={stage.is_optional ? "secondary" : "default"}>
                      {stage.is_optional ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(stage)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(stage.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ConversationStageDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        stage={selectedStage}
        onSuccess={() => {
          refetch();
          setIsDialogOpen(false);
        }}
      />
    </div>
  );
};